import { useEffect, useRef } from "react";
import { isAllDay, type AgendaDates } from "@/lib/agendaDates";

/** The snapshot's local day, not 24 elapsed hours (DST days differ). */
export function nextAgendaRefreshAt(checkedAt: number, event: AgendaDates | null): number {
  const checked = new Date(checkedAt);
  const midnight = new Date(checked.getFullYear(), checked.getMonth(), checked.getDate() + 1).getTime();
  const start = event && !isAllDay(event) ? Date.parse(event.start_at) : NaN;
  return Number.isFinite(start) && start > checkedAt ? Math.min(start, midnight) : midnight;
}

interface Options {
  checkedAt: number | null;
  nextEvent: AgendaDates | null;
  loading: boolean;
  error: boolean;
  enabled?: boolean;
  refresh: () => unknown;
}

/** One deadline per confirmed snapshot, plus foreground/connection recovery.
 * No interval polling and no requests while hidden, offline or already loading. */
export function useAgendaAutoRefresh({ checkedAt, nextEvent, loading, error, enabled = true, refresh }: Options) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const pending = useRef(false);
  const lastResume = useRef(-Infinity);
  const handledBoundary = useRef<{ checkedAt: number | null; deadline: number | null } | null>(null);
  const deadline = checkedAt === null ? null : nextAgendaRefreshAt(checkedAt, nextEvent);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const available = () => document.visibilityState !== "hidden" && navigator.onLine !== false;
    const request = (resume: boolean) => {
      if (!active || loading || pending.current || !available()) return;
      if (!resume && handledBoundary.current?.checkedAt === checkedAt && handledBoundary.current?.deadline === deadline) return;
      // Browsers commonly emit visibility, focus and online for one return.
      // Deadline refreshes remain independent of this short resume debounce.
      if (resume && Date.now() - lastResume.current < 1_000) return;
      lastResume.current = Date.now();
      if (deadline !== null && deadline <= Date.now()) handledBoundary.current = { checkedAt, deadline };
      pending.current = true;
      try {
        const result = refreshRef.current();
        void Promise.resolve(result).catch(() => {
          // The owner hook exposes its existing error/retry state.
        }).finally(() => { pending.current = false; });
      } catch {
        pending.current = false;
      }
    };
    const clearTimer = () => { if (timer !== undefined) clearTimeout(timer); };
    const armTimer = () => {
      if (!loading && !error && deadline !== null && available()) {
        // A query that finishes after its deadline is refreshed once it settles.
        timer = setTimeout(() => request(false), Math.max(0, deadline - Date.now()));
      }
    };
    const onReturn = () => {
      clearTimer();
      if (available()) request(true);
      // A coalesced focus/online event must not cancel the next deadline.
      armTimer();
    };
    armTimer();
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    window.addEventListener("online", onReturn);
    window.addEventListener("offline", onReturn);
    return () => {
      active = false;
      clearTimer();
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
      window.removeEventListener("online", onReturn);
      window.removeEventListener("offline", onReturn);
    };
  }, [checkedAt, deadline, loading, error, enabled]);
}
